import {useMemo, useState} from "react";
import {App, Avatar, Checkbox, Dropdown, Flex, Modal, Switch, Typography} from "antd";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import classname from "classname";

import {bulkUpdateRequestsEnrollment} from "../../redux/requestsEnrollments.thunks";
import Icon from "../../../../../Icon";
import ButtonComponent from "../../../../../Components/Button";
import {
  descriptorStatuses,
  descriptorStatusTextMap,
  descriptorStatusTypeMap,
} from "../../../../ConfigurePage/utils/Enrollment.util";

import "./BulkEditModal.less";

const {Text} = Typography;

const MATCH_OPTIONS = [
  {key: "exact", label: "Exact match"},
  {key: "starts_with", label: "Starts with"},
];

const RDR_ONLY_STATUSES = new Set(["invalid_arns", "needs_assistance"]);
const RDR_ONLY_STATUS_ENROLLMENTS = new Set(["ethoca", "cdrn"]);

const getStatusLabel = (key) => descriptorStatusTextMap[key] || key;
const getStatusType = (key) => descriptorStatusTypeMap[key] || "pending";
const getMatchLabel = (key) =>
  MATCH_OPTIONS.find((item) => item.key === key)?.label || key || "Exact match";

const getEnrollmentKey = (request) =>
  (request?.enrollments || request?.enrollment || "").toLowerCase();

const toPlainRow = (r) => ({
  id: r.id,
  merchant_descriptor: r.merchant_descriptor,
  business_account: r.business_account,
  enrollments: r.enrollments || r.enrollment,
  request_status: r.request_status,
  match_type: r.match_type,
});

const BulkEditModalBody = ({requests, onClose, onApplied}) => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {notification} = App.useApp();

  const [rows] = useState(() => (requests || []).map(toPlainRow));
  // staged per-row changes: { [id]: { request_status?, match_type? } }
  const [overrides, setOverrides] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [bulkMatch, setBulkMatch] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [applying, setApplying] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const allSelected = rows.length > 0 && selectedIds.length === rows.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const statusOf = (row) => overrides[row.id]?.request_status ?? row.request_status;
  const matchOf = (row) => overrides[row.id]?.match_type ?? row.match_type;

  const hasChanges = rows.some((row) => {
    const ov = overrides[row.id];
    if (!ov) return false;
    return (
      (ov.request_status && ov.request_status !== row.request_status) ||
      (ov.match_type && ov.match_type !== row.match_type)
    );
  });

  const applyEnabled = hasChanges && !applying;

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? rows.map((row) => row.id) : []);
  };

  const handleToggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const stageValue = (ids, field, value) => {
    setOverrides((prev) => {
      const next = {...prev};
      ids.forEach((id) => {
        next[id] = {...next[id], [field]: value};
      });
      return next;
    });
  };

  const handleBulkStatus = (key) => {
    if (!selectedIds.length) return;
    setBulkStatus(key);
    stageValue(selectedIds, "request_status", key);
  };

  const handleBulkMatch = (key) => {
    if (!selectedIds.length) return;
    setBulkMatch(key);
    stageValue(selectedIds, "match_type", key);
  };

  const buildGroups = () => {
    const groups = {};
    rows.forEach((row) => {
      const ov = overrides[row.id];
      if (!ov) return;

      const payload = {};
      if (ov.request_status && ov.request_status !== row.request_status) {
        payload.request_status = ov.request_status;
      }
      if (ov.match_type && ov.match_type !== row.match_type) {
        payload.match_type = ov.match_type;
      }
      if (!Object.keys(payload).length) return;

      const sig = JSON.stringify(payload);
      if (!groups[sig]) groups[sig] = {payload, ids: []};
      groups[sig].ids.push(row.id);
    });
    return Object.values(groups);
  };

  const handleApply = async () => {
    const groups = buildGroups();
    if (!groups.length || applying) return;

    const hasRdrOnlyStatusForUnsupportedEnrollment = rows.some((row) => {
      const nextStatus = overrides[row.id]?.request_status;

      return (
        nextStatus &&
        nextStatus !== row.request_status &&
        RDR_ONLY_STATUSES.has(nextStatus) &&
        RDR_ONLY_STATUS_ENROLLMENTS.has(getEnrollmentKey(row))
      );
    });

    if (hasRdrOnlyStatusForUnsupportedEnrollment) {
      notification.error({
        message: t(
          "You can set Invalid ARN/BIN/CAID and Needs Assistance statuses only for RDR enrollment."
        ),
        closeIcon: <Text>{t("Dismiss")}</Text>,
      });
      return;
    }

    setApplying(true);

    try {
      // sequential so state updates from earlier groups land before later ones
      for (const group of groups) {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(
          bulkUpdateRequestsEnrollment({
            enrollment_request_ids: group.ids,
            ...group.payload,
            send_email: sendEmail,
          })
        ).unwrap();
      }

      setApplying(false);
      notification.success({
        message: t("Enrollment requests statuses updated"),
        closeIcon: <Text>{t("Dismiss")}</Text>,
      });
      onApplied?.();
      onClose();
    } catch (error) {
      setApplying(false);
      notification.error({
        message: t("Enrollment requests statuses were not updated"),
        description:
          error?.message?.detail ||
          error?.detail ||
          error?.request_status ||
          t("Something went wrong"),
        closeIcon: <Text>{t("Dismiss")}</Text>,
      });
    }
  };

  const statusMenuItems = descriptorStatuses.map((key) => ({
    key,
    label: (
      <Flex align="center" gap={12}>
        <span
          className={classname("bulk-edit-dot", `bulk-edit-dot-${getStatusType(key)}`)}
        />
        <span>{t(getStatusLabel(key))}</span>
      </Flex>
    ),
  }));

  const matchMenuItems = MATCH_OPTIONS.map((item) => ({
    key: item.key,
    label: t(item.label),
  }));

  const renderRowStatus = (row) => {
    const statusKey = statusOf(row);

    return (
      <Dropdown
        trigger={["click"]}
        menu={{
          items: statusMenuItems,
          onClick: ({key}) => stageValue([row.id], "request_status", key),
        }}
        overlayClassName="bulk-edit-dropdown"
      >
        <Flex
          align="center"
          justify="flex-end"
          gap={8}
          className="bulk-edit-row-trigger"
        >
          <Text
            strong
            className={classname(
              "bulk-edit-row-status",
              `bulk-edit-status-${getStatusType(statusKey)}`
            )}
          >
            {t(getStatusLabel(statusKey))}
          </Text>
          <Icon type="chevron_down_small" className="bulk-edit-arrow" />
        </Flex>
      </Dropdown>
    );
  };

  const renderRowMatch = (row) => {
    const matchValue = matchOf(row);

    return (
      <Dropdown
        trigger={["click"]}
        menu={{
          items: matchMenuItems,
          onClick: ({key}) => stageValue([row.id], "match_type", key),
        }}
        overlayClassName="bulk-edit-dropdown"
      >
        <Flex
          align="center"
          justify="flex-end"
          gap={8}
          className="bulk-edit-row-trigger"
        >
          <Text type="secondary" strong className="bulk-edit-row-match">
            {t(getMatchLabel(matchValue))}
          </Text>
          <Icon type="chevron_down_small" className="bulk-edit-arrow" />
        </Flex>
      </Dropdown>
    );
  };

  return (
    <div className="bulk-edit-modal">
      <div className="bulk-edit-header">
        <Text className="bulk-edit-title">
          {t("Bulk edit billing descriptors")}
        </Text>
        <Text type="secondary" className="bulk-edit-subtitle">
          {t(
            "Select descriptors to update their status or enrollment. Bulk changes apply to all selected descriptors."
          )}
        </Text>
      </div>

      <Flex
        align="center"
        justify="space-between"
        gap={12}
        className="bulk-edit-controls"
      >
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="bulk-edit-select-all"
        >
          {t("Select all")}
        </Checkbox>

        <Flex align="center" gap={12} className="bulk-edit-actions">
          <Dropdown
            disabled={!selectedIds.length}
            trigger={["click"]}
            menu={{
              items: statusMenuItems,
              onClick: ({key}) => handleBulkStatus(key),
            }}
            overlayClassName="bulk-edit-dropdown bulk-edit-status-dropdown"
          >
            <Flex
              align="center"
              justify="space-between"
              gap={8}
              className={classname("bulk-edit-trigger", {
                "bulk-edit-trigger-disabled": !selectedIds.length,
              })}
            >
              {bulkStatus ? (
                <Flex align="center" gap={8}>
                  <span
                    className={classname(
                      "bulk-edit-dot",
                      `bulk-edit-dot-${getStatusType(bulkStatus)}`
                    )}
                  />
                  <Text strong>{t(getStatusLabel(bulkStatus))}</Text>
                </Flex>
              ) : (
                <Text type="secondary" strong>
                  {t("Set status")}
                </Text>
              )}
              <Icon type="chevron_down_small" className="bulk-edit-arrow" />
            </Flex>
          </Dropdown>

          <Dropdown
            disabled={!selectedIds.length}
            trigger={["click"]}
            menu={{
              items: matchMenuItems,
              onClick: ({key}) => handleBulkMatch(key),
            }}
            overlayClassName="bulk-edit-dropdown"
          >
            <Flex
              align="center"
              justify="space-between"
              gap={8}
              className={classname("bulk-edit-trigger", {
                "bulk-edit-trigger-disabled": !selectedIds.length,
              })}
            >
              <Text type={bulkMatch ? undefined : "secondary"} strong>
                {bulkMatch ? t(getMatchLabel(bulkMatch)) : t("Set match")}
              </Text>
              <Icon type="chevron_down_small" className="bulk-edit-arrow" />
            </Flex>
          </Dropdown>
        </Flex>
      </Flex>

      <div className="bulk-edit-list">
        {rows.map((row) => {
          const id = row.id;
          const enrollmentKey = getEnrollmentKey(row);
          const enrollmentLabel =
            enrollmentKey === "ethoca" ? "Ethoca" : enrollmentKey.toUpperCase();
          const enrollmentIcon =
            enrollmentKey === "ethoca" ? "mastercard" : "visa";
          const descriptorName = Array.isArray(row.merchant_descriptor)
            ? row.merchant_descriptor.join(", ")
            : row.merchant_descriptor;

          return (
            <Flex
              key={id}
              align="center"
              justify="space-between"
              gap={16}
              className="bulk-edit-row"
            >
              <Checkbox
                checked={selectedSet.has(id)}
                onChange={() => handleToggleRow(id)}
                className="bulk-edit-row-check"
              />

              <Flex align="center" gap={12} className="bulk-edit-row-main">
                <Text strong className="bulk-edit-row-name" ellipsis>
                  {descriptorName}
                </Text>

                <span className="bulk-edit-row-divider" />

                <Flex align="center" gap={8} className="bulk-edit-row-ba">
                  <Avatar size={20} className="bulk-edit-avatar">
                    {(row.business_account?.name || "?").slice(0, 2).toUpperCase()}
                  </Avatar>
                  <Text type="secondary" ellipsis>
                    {row.business_account?.name}
                  </Text>
                </Flex>

                <span className="bulk-edit-row-divider" />

                <Flex align="center" gap={8} className="bulk-edit-row-enrollment">
                  <Icon type={enrollmentIcon} className="bulk-edit-enrollment-icon" />
                  <Text type="secondary">{enrollmentLabel}</Text>
                </Flex>
              </Flex>

              <Flex align="center" gap={4} className="bulk-edit-row-values">
                {renderRowStatus(row)}
                <span className="bulk-edit-row-divider" />
                {renderRowMatch(row)}
              </Flex>
            </Flex>
          );
        })}
      </div>

      <Flex align="center" className="bulk-edit-email">
        <Switch checked={sendEmail} onChange={setSendEmail} />
        <Text className="bulk-edit-email-label">
          {t("Send an email about the status change")}
        </Text>
      </Flex>

      <Flex
        align="center"
        justify="space-between"
        gap={12}
        className="bulk-edit-footer"
      >
        <Text type="secondary" className="bulk-edit-counter">
          <Text
            strong
            className={classname("bulk-edit-counter-value", {
              "bulk-edit-counter-value-empty": selectedIds.length === 0,
            })}
          >
            {selectedIds.length}
          </Text>{" "}
          {t("descriptors selected")}
        </Text>

        <Flex align="center" gap={12}>
          <ButtonComponent size="large" onClick={onClose} className="bulk-edit-cancel">
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent
            size="large"
            type="primary"
            disabled={!applyEnabled}
            loading={applying}
            onClick={handleApply}
          >
            {t("Apply")}
          </ButtonComponent>
        </Flex>
      </Flex>
    </div>
  );
};

const BulkEditModal = ({open, onClose, requests = [], onApplied}) => {
  const {t} = useTranslation();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={750}
      centered
      closeIcon={<Icon type="close_modal" />}
      title={null}
      className="bulk-edit-modal-root"
      classNames={{content: "clear-bg"}}
      aria-label={t("Bulk edit billing descriptors")}
    >
        <BulkEditModalBody
          requests={requests}
          onClose={onClose}
          onApplied={onApplied}
        />
    </Modal>
  );
};

export default BulkEditModal;
